import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import { Box, TextField, Select, Button, IconButton, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { BsTrash } from "react-icons/bs";
import { ImageIcon, X } from "lucide-react";
import { createVisitaInstalacion, getAlmacenes, getProductosKaeAlmacen } from "../../../../services";
import { comprimirImagen, comprimirImagenOld } from "../../../../functions/funciones";
import LaeButton from "../../../../components/Buttons/LaeButton";

const MAX_IMAGENES = 10;
const MAX_TAMANO_BYTES = 10 * 1024 * 1024;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: "90vw", md: "85ch" },
  maxWidth: "85ch",
  maxHeight: { xs: "92vh", sm: "85vh" },
  overflow: "auto",
  bgcolor: "background.paper",
  border: { xs: "1px solid #000", sm: "2px solid #000" },
  boxShadow: 24,
};

const RealizarInstalacion = ({ instalacion, opened, handleClose }) => {
  const { t } = useTranslation(["common"]);
  const [almacenes, setAlmacenes] = useState([]);
  const [productosCache, setProductosCache] = useState({});
  const [listaItems, setListaItems] = useState([]);
  const [observaciones, setObservaciones] = useState("");
  const [imagenes, setImagenes] = useState([]);
  const [procesandoImagenes, setProcesandoImagenes] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const fileInputRef = useRef(null);

  const senalesObligatorias = (instalacion?.productos || []).filter((p) => p.obligatorio);
  const estaInstalada = (senal) =>
    (senal.almacen_id && senal.cantidad > 0) ||
    listaItems.some((item) => Number(item.producto_id) === senal.producto_id && Number(item.cantidad) > 0);
  const faltanSenales = senalesObligatorias.filter((senal) => !estaInstalada(senal));

  useEffect(() => {
    if (opened) {
      cargarAlmacenes();
      setProductosCache({});
      setListaItems([]);
      setObservaciones("");
      imagenes.forEach((img) => img.url && URL.revokeObjectURL(img.url));
      setImagenes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  useEffect(() => {
    return () => {
      imagenes.forEach((img) => img.url && URL.revokeObjectURL(img.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarAlmacenes = async () => {
    try {
      const res = await getAlmacenes();
      setAlmacenes(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
    }
  };

  const cargarProductosAlmacen = async (almacenId) => {
    if (productosCache[almacenId]) return productosCache[almacenId];
    try {
      const res = await getProductosKaeAlmacen(almacenId);
      const items = Array.isArray(res) ? res : [];
      setProductosCache((prev) => ({ ...prev, [almacenId]: items }));
      return items;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getProductosDisponibles = (currentIndex) => {
    const item = listaItems[currentIndex];
    if (!item.almacen_id) return [];
    const productos = productosCache[item.almacen_id] || [];
    return productos.filter(
      (p) => !listaItems.some((s, i) => i !== currentIndex && s.almacen_id === item.almacen_id && s.producto_id === p.id)
    );
  };

  const handleAddItem = () => {
    setListaItems([
      ...listaItems,
      { almacen_id: "", producto_id: "", nombre: "", cantidad: "", stockDisponible: 0 },
    ]);
  };

  const handleRemoveItem = (index) => {
    setListaItems(listaItems.filter((_, i) => i !== index));
  };

  const handleInputChange = (index, event) => {
    const { name, value } = event.target;
    const updated = [...listaItems];
    if (name === "cantidad") {
      const num = value === "" ? "" : Math.max(0, Math.min(Number(value), updated[index].stockDisponible || Infinity));
      updated[index] = { ...updated[index], [name]: num };
    } else {
      updated[index] = { ...updated[index], [name]: Number(value) };
    }
    setListaItems(updated);
  };

  const handleSelectAlmacen = async (index, event) => {
    const almacenId = event.target.value;
    const updated = [...listaItems];
    updated[index] = { ...updated[index], almacen_id: almacenId, producto_id: "", nombre: "", cantidad: "", stockDisponible: 0 };
    setListaItems(updated);
    if (almacenId) {
      await cargarProductosAlmacen(almacenId);
    }
  };

  const handleSelectProducto = (index, event) => {
    const productoId = parseInt(event.target.value);
    const item = listaItems[index];
    const productos = productosCache[item.almacen_id] || [];
    const producto = productos.find((p) => p.id === productoId);
    const updated = [...listaItems];
    updated[index] = {
      ...updated[index],
      producto_id: productoId,
      nombre: producto?.nombre || "",
      stockDisponible: producto?.stocks?.[0]?.cantidad || 0,
    };
    setListaItems(updated);
  };

  const onClickAddImagenes = () => {
    if (procesandoImagenes || enviando) return;
    fileInputRef.current?.click();
  };

  const esMovil = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const esMovilUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const esPantallaPequena = window.innerWidth <= 768 || window.innerHeight <= 600;
    const esTactil = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const indicadores = [esMovilUserAgent, esPantallaPequena, esTactil].filter(Boolean).length;
    return indicadores >= 2;
  };

  // Mismo patrón que CrearHojaVisita/EditarHojaVisita: Resizer primero con
  // timeout de 20s; si falla o tarda, fallback a canvas.toBlob con timeout 10s.
  const comprimirConTimeout = (file) => {
    const comprimirImagenOldConTimeout = (f) => {
      return new Promise((resolve, reject) => {
        let terminado = false;
        const timerOld = setTimeout(() => {
          if (!terminado) {
            terminado = true;
            reject(new Error("Timeout en compresión antigua"));
          }
        }, 10000);
        comprimirImagenOld(f)
          .then((blob) => {
            if (!terminado) {
              terminado = true;
              clearTimeout(timerOld);
              resolve(blob);
            }
          })
          .catch((err) => {
            terminado = true;
            clearTimeout(timerOld);
            reject(err);
          });
      });
    };

    return new Promise(async (resolve, reject) => {
      let terminado = false;
      const timer = setTimeout(async () => {
        if (!terminado) {
          terminado = true;
          try {
            const blobOld = await comprimirImagenOldConTimeout(file);
            resolve(blobOld);
          } catch (err) {
            reject(err);
          }
        }
      }, 20000);

      try {
        const blob = await comprimirImagen(file);
        if (!terminado) {
          terminado = true;
          clearTimeout(timer);
          resolve(blob);
        }
      } catch (err) {
        clearTimeout(timer);
        try {
          const blobOld = await comprimirImagenOldConTimeout(file);
          resolve(blobOld);
        } catch (errOld) {
          reject(errOld);
        }
      }
    });
  };

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const disponibles = Math.max(0, MAX_IMAGENES - imagenes.length);
    if (disponibles === 0) {
      toast.error(t("installations.images.tooManyImages", { max: MAX_IMAGENES }));
      return;
    }
    const aProcesar = files.slice(0, disponibles);
    if (files.length > aProcesar.length) {
      toast.warn(t("installations.images.tooManyImages", { max: MAX_IMAGENES }));
    }

    const dispositivoMovil = esMovil();
    const archivosValidos = aProcesar.filter((file) => {
      if (!file.type?.startsWith("image/")) {
        toast.error(t("installations.images.invalidType", { name: file.name }));
        return false;
      }
      if (file.size > MAX_TAMANO_BYTES) {
        toast.error(t("installations.images.tooLarge", { name: file.name }));
        return false;
      }
      return true;
    });
    if (archivosValidos.length === 0) return;

    setProcesandoImagenes(true);
    const nuevas = [];

    const procesarArchivo = async (file) => {
      try {
        let finalFile = file;
        if (file.size > 500 * 1024) {
          const blob = await comprimirConTimeout(file);
          const nombreArchivo = file.name.split('.').slice(0, -1).join('.') + '.webp';
          finalFile = new File([blob], nombreArchivo, { type: 'image/webp' });
        }
        nuevas.push({ file: finalFile, url: URL.createObjectURL(finalFile), name: finalFile.name });
      } catch (err) {
        console.error("Error procesando imagen", file.name, err);
        toast.error(t("installations.images.processingError", { name: file.name }));
      }
    };

    try {
      if (dispositivoMovil) {
        // Móvil: secuencial — menos picos de RAM en dispositivos flojos.
        for (let i = 0; i < archivosValidos.length; i++) {
          await procesarArchivo(archivosValidos[i]);
        }
      } else {
        // PC: en paralelo — más rápido.
        await Promise.all(archivosValidos.map((file) => procesarArchivo(file)));
      }
    } finally {
      if (nuevas.length) {
        setImagenes((prev) => [...prev, ...nuevas]);
      }
      setProcesandoImagenes(false);
    }
  };

  const removeImagen = (idx) => {
    setImagenes((prev) => {
      const copia = [...prev];
      const [eliminada] = copia.splice(idx, 1);
      if (eliminada?.url) URL.revokeObjectURL(eliminada.url);
      return copia;
    });
  };

  const handleSubmit = async () => {
    if (enviando) return;
    const toastInfo = toast.info(t("installations.messages.saving"), { autoClose: false });
    setEnviando(true);
    try {
      const productos = listaItems
        .filter((item) => item.producto_id && item.almacen_id && item.cantidad > 0)
        .map((item) => ({
          producto_id: item.producto_id,
          almacen_id: Number(item.almacen_id),
          cantidad: item.cantidad,
        }));

      if (productos.length === 0) {
        toast.update(toastInfo, {
          render: t("installations.messages.emptyInstallation"),
          type: toast.TYPE.ERROR,
          autoClose: true,
        });
        setEnviando(false);
        return;
      }

      const data = {
        instalacion_id: instalacion?.id,
        productos,
        observaciones: observaciones.trim() || undefined,
      };
      const result = await createVisitaInstalacion(data, imagenes.map((i) => i.file));
      console.log(result);
      if (result.status === 201) {
        toast.update(toastInfo, {
          render: t("installations.messages.editedSuccess"),
          type: toast.TYPE.SUCCESS,
          autoClose: true,
        });
        handleClose();
      } else if (result.status === 400) {
        // El backend no deja cerrar la visita si falta alguna señal obligatoria
        const faltan = result.data?.error === "FALTAN_SENALES_OBLIGATORIAS" ? result.data.senales : null;
        toast.update(toastInfo, {
          render: faltan
            ? t("installations.messages.missingMandatorySignage", {
                senales: faltan.map((s) => s.sku || s.nombre).join(", "),
              })
            : t("installations.messages.cuantityerror"),
          type: toast.TYPE.ERROR,
          autoClose: true,
        });
      } else {
        toast.update(toastInfo, {
          render: t("installations.messages.editError"),
          type: toast.TYPE.ERROR,
          autoClose: true,
        });
      }
    } catch (err) {
      console.log(err);
      toast.update(toastInfo, {
        render: t("installations.messages.editError"),
        type: toast.TYPE.ERROR,
        autoClose: true,
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        open={opened}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Box sx={style}>
          <Box
            sx={{
              backgroundColor: "#fff",
              border: { xs: "1px solid #000", sm: "2px solid #000" },
              boxShadow:
                "0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
              padding: { xs: "12px 14px 16px", sm: "16px 32px 24px" },
            }}
          >
            <h2 id="transition-modal-title">{t("installations.columns.performVisit")}</h2>
            {senalesObligatorias.length > 0 && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: faltanSenales.length > 0 ? "#f0a000" : "#3eae3e",
                  bgcolor: faltanSenales.length > 0 ? "#fff8e1" : "#f1f8e9",
                }}
              >
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t("installations.mandatorySignage")}
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "#666", mb: 1 }}>
                  {t("installations.mandatorySignageTechHint")}
                </Typography>
                {senalesObligatorias.map((senal) => {
                  const puesta = estaInstalada(senal);
                  return (
                    <Box key={senal.id} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.9rem" }}>
                      <span style={{ color: puesta ? "#3eae3e" : "#b26a00", fontWeight: 700 }}>
                        {puesta ? "\u2713" : "\u25cb"}
                      </span>
                      <span style={{ textDecoration: puesta ? "line-through" : "none", color: puesta ? "#666" : "#333" }}>
                        {senal.producto?.sku} - {senal.producto?.nombre}
                      </span>
                    </Box>
                  );
                })}
              </Box>
            )}
            <div id="transition-modal-description">
              {listaItems.length === 0 ? (
                <div style={{ margin: 15 }}>
                  <p>{t("products.messages.noProducts")}</p>
                </div>
              ) : (
                listaItems.map((item, index) => (
                  <div key={index}>
                    <Box
                      mx={1}
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      gap={{ xs: 1, sm: 0 }}
                      mb={{ xs: 2, sm: 0 }}
                    >
                      <Select
                        native
                        name="almacen_id"
                        sx={{ width: { xs: "100%", sm: "30%" }, margin: { xs: 0, sm: "5px" } }}
                        value={item.almacen_id || ""}
                        onChange={(e) => handleSelectAlmacen(index, e)}
                      >
                        <option value="">{t("almacenes.title")}</option>
                        {almacenes.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.nombre}
                          </option>
                        ))}
                      </Select>
                      <Select
                        native
                        name="producto_id"
                        sx={{ width: { xs: "100%", sm: "40%" }, margin: { xs: 0, sm: "5px" } }}
                        value={item.producto_id || ""}
                        onChange={(e) => handleSelectProducto(index, e)}
                        disabled={!item.almacen_id}
                      >
                        <option value="">{t("products.titleSingular")}</option>
                        {getProductosDisponibles(index).map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nombre} (stock: {p.stocks?.[0]?.cantidad || 0})
                          </option>
                        ))}
                      </Select>
                      <Box display="flex" alignItems="center" sx={{ width: { xs: "100%", sm: "auto" } }}>
                        <TextField
                          sx={{ width: { xs: "100%", sm: 110 }, margin: { xs: 0, sm: "5px" } }}
                          type="number"
                          label={t("products.columns.cuantity")}
                          variant="outlined"
                          name="cantidad"
                          onChange={(e) => handleInputChange(index, e)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ min: 0, max: item.stockDisponible || undefined }}
                          value={item.cantidad}
                        />
                        <button
                          onClick={() => handleRemoveItem(index)}
                          style={{
                            backgroundColor: "transparent",
                            color: "#000",
                            height: 30,
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          <BsTrash />
                        </button>
                      </Box>
                    </Box>
                  </div>
                ))
              )}
              <Box mx={2} my={3} display={"flex"} flexDirection={"row"}>
                <button
                  className="btn-Secondary"
                  style={{ color: "white", padding: "6px 12px", border: "none", borderRadius: "4px", cursor: "pointer" }}
                  onClick={handleAddItem}
                >
                  {t("actions.add")} {t("products.titleSingular").toLowerCase()}
                </button>
              </Box>

              <Box mx={1} my={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  label={t("installations.detail.observations")}
                  variant="outlined"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  inputProps={{ maxLength: 500 }}
                />
              </Box>

              <Box mx={1} my={2}>
                <Box
                  display="flex"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={1}
                  mb={1}
                >
                  <span style={{ fontWeight: 600, fontSize: 14 }}>
                    {t("installations.images.title")} ({imagenes.length}/{MAX_IMAGENES})
                  </span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ImageIcon size={16} />}
                    onClick={onClickAddImagenes}
                    disabled={procesandoImagenes || imagenes.length >= MAX_IMAGENES}
                    sx={{ color: "#92004D", borderColor: "#92004D", alignSelf: { xs: "stretch", sm: "auto" }, "&:hover": { borderColor: "#92004D", backgroundColor: "rgba(146,0,77,0.04)" } }}
                  >
                    {procesandoImagenes ? t("installations.images.processing") : t("installations.images.addButton")}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFilesSelected}
                  />
                </Box>
                {imagenes.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {imagenes.map((img, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          position: "relative",
                          width: { xs: 80, sm: 90 },
                          height: { xs: 80, sm: 90 },
                          borderRadius: 1,
                          overflow: "hidden",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <img
                          src={img.url}
                          alt={img.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeImagen(idx)}
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            backgroundColor: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            padding: "2px",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                          }}
                        >
                          <X size={12} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <div
                style={{
                  margin: "8px",
                  marginTop: "16px",
                  position: "relative",
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gridGap: "15px",
                  fontSize: "12px",
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    flex: 1,
                    backgroundColor: "whitesmoke",
                    color: "#92004D",
                    ":hover": { backgroundColor: "#92004D", color: "white" },
                    margin: 0,
                    p: 1,
                  }}
                  disableElevation
                  onClick={handleClose}
                >
                  {t("actions.cancel")}
                </Button>
                <LaeButton sx={{ flex: 1 }} onClick={handleSubmit} disabled={enviando || procesandoImagenes || faltanSenales.length > 0}>
                  {t("actions.save")}
                </LaeButton>
              </div>
            </div>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default RealizarInstalacion;
